import { isAccountsOnly, isLogisticsOnly, isProjectsOnly, type SessionInfo } from '@crm/shared';
import { useQuery } from '@tanstack/react-query';
import { type ComponentType, lazy, Suspense, useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router';
import { AppShell } from './components/AppShell';
import { api, ApiError } from './lib/api';
import { SessionProvider } from './lib/session';
import { AcceptInvite, ForgotPassword, ResetPassword } from './pages/AuthFlows';
import { Home, PresalesQueuePage } from './pages/Home';
import { Login } from './pages/Login';

// Pages load when first opened, so the first visit downloads less (Login and Home come at once). Once the CRM is idle,
// the other pages download quietly in the background, so moving to them later is instant.
const loaders: (() => Promise<unknown>)[] = [];
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the same bound as React's own lazy()
function page<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  loaders.push(load);
  return lazy(() => load().catch((e: unknown) => {
    // Open during an update: this page's old file is gone from the server. Reload once to get the new version.
    try {
      if (sessionStorage.getItem('page-reload') !== location.pathname) { sessionStorage.setItem('page-reload', location.pathname); location.reload(); }
    } catch { /* no storage: show the error */ }
    throw e;
  }));
}
function usePrefetchPages() {
  useEffect(() => {
    const idle = (fn: () => void) => (typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback(fn, { timeout: 8000 }) : window.setTimeout(fn, 3000));
    idle(() => { for (const load of loaders) void load().catch(() => undefined); });
  }, []);
}
/** While a page is loading for the first time: grey shimmering lines where it will be. */
const pageLoading = <p className="is-loading" aria-label="Loading" />;

const CustomListView = page(() => import('./pages/CustomObjects').then((m) => ({ default: m.CustomListView })));
const CustomRecordPage = page(() => import('./pages/CustomObjects').then((m) => ({ default: m.CustomRecordPage })));
const Guide = page(() => import('./pages/Guide').then((m) => ({ default: m.Guide })));
const Import = page(() => import('./pages/Import').then((m) => ({ default: m.Import })));
const DashboardPage = page(() => import('./pages/Dashboards').then((m) => ({ default: m.DashboardPage })));
const Dashboards = page(() => import('./pages/Dashboards').then((m) => ({ default: m.Dashboards })));
const Forecasts = page(() => import('./pages/Forecasts').then((m) => ({ default: m.Forecasts })));
const Intelligence = page(() => import('./pages/Intelligence').then((m) => ({ default: m.Intelligence })));
const HistoryReadinessPage = page(() => import('./pages/HistoryReadiness').then((m) => ({ default: m.HistoryReadinessPage })));
const CeoPage = page(() => import('./pages/Ceo').then((m) => ({ default: m.CeoPage })));
const PurchasingPage = page(() => import('./pages/Purchasing').then((m) => ({ default: m.PurchasingPage })));
const ExpensesPage = page(() => import('./pages/Expenses').then((m) => ({ default: m.ExpensesPage })));
const CompliancePage = page(() => import('./pages/Compliance').then((m) => ({ default: m.CompliancePage })));
const CompliancePersonPage = page(() => import('./pages/Compliance').then((m) => ({ default: m.CompliancePersonPage })));
const ExceptionsPage = page(() => import('./pages/Exceptions').then((m) => ({ default: m.ExceptionsPage })));
const ReportBuilder = page(() => import('./pages/Reports').then((m) => ({ default: m.ReportBuilder })));
const Reports = page(() => import('./pages/Reports').then((m) => ({ default: m.Reports })));
const SalespersonReport = page(() => import('./pages/SalespersonReport').then((m) => ({ default: m.SalespersonReport })));
const WeeklySalesPage = page(() => import('./pages/WeeklySales').then((m) => ({ default: m.WeeklySalesPage })));
const OdooLinksPage = page(() => import('./pages/OdooLinks').then((m) => ({ default: m.OdooLinksPage })));
const ProfitOverviewPage = page(() => import('./pages/ProfitOverview').then((m) => ({ default: m.ProfitOverviewPage })));
const QuotePage = page(() => import('./pages/QuotePage').then((m) => ({ default: m.QuotePage })));
const ForcedMfa = page(() => import('./pages/Settings').then((m) => ({ default: m.ForcedMfa })));
const Settings = page(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));
const SuggestionsPage = page(() => import('./pages/Suggestions').then((m) => ({ default: m.SuggestionsPage })));
const ListView = page(() => import('./pages/ListView').then((m) => ({ default: m.ListView })));
const OAuthConsent = page(() => import('./pages/OAuthConsent').then((m) => ({ default: m.OAuthConsent })));
const RecordPage = page(() => import('./pages/RecordPage').then((m) => ({ default: m.RecordPage })));
/** A new record page for each record: nothing (an open editor, an error) carries over from the previous one. */
function KeyedRecordPage() { const { object, id } = useParams(); return <RecordPage key={`${object}/${id}`} />; }
const RecycleBin = page(() => import('./pages/RecycleBin').then((m) => ({ default: m.RecycleBin })));
const Setup = page(() => import('./pages/Setup').then((m) => ({ default: m.Setup })));
const KpiPage = page(() => import('./pages/Kpi').then((m) => ({ default: m.KpiPage })));
const CalendarPage = page(() => import('./pages/Calendar').then((m) => ({ default: m.CalendarPage })));
const ProjectsPage = page(() => import('./pages/Projects').then((m) => ({ default: m.ProjectsPage })));
const ProjectPage = page(() => import('./pages/Project').then((m) => ({ default: m.ProjectPage })));
const ProjectReport = page(() => import('./pages/ProjectReport').then((m) => ({ default: m.ProjectReport })));
const ProjectPeriodReport = page(() => import('./pages/ProjectPeriodReport').then((m) => ({ default: m.ProjectPeriodReport })));
const ProjectTimelinePrint = page(() => import('./pages/ProjectTimelinePrint').then((m) => ({ default: m.ProjectTimelinePrint })));
const OrderRedirect = page(() => import('./pages/OrderRedirect').then((m) => ({ default: m.OrderRedirect })));
const VariationRedirect = page(() => import('./pages/OrderRedirect').then((m) => ({ default: m.VariationRedirect })));
const ProjectPortfolio = page(() => import('./pages/ProjectPortfolio').then((m) => ({ default: m.ProjectPortfolio })));
const ManpowerPage = page(() => import('./pages/Manpower').then((m) => ({ default: m.ManpowerPage })));
const ToolsPage = page(() => import('./pages/Tools').then((m) => ({ default: m.ToolsPage })));
const AccountsPage = page(() => import('./pages/Accounts').then((m) => ({ default: m.AccountsPage })));
const PriorityRedirect = page(() => import('./pages/PriorityRedirect').then((m) => ({ default: m.PriorityRedirect })));
const ProjectsHomePage = page(() => import('./pages/ProjectsHome').then((m) => ({ default: m.ProjectsHomePage })));
const LogisticsPage = page(() => import('./pages/Logistics').then((m) => ({ default: m.LogisticsPage })));
const PresalesPage = page(() => import('./pages/Presales').then((m) => ({ default: m.PresalesPage })));
const SiteCheckinPage = page(() => import('./pages/SiteCheckin').then((m) => ({ default: m.SiteCheckinPage })));
const WorkerPassPage = page(() => import('./pages/WorkerPass').then((m) => ({ default: m.WorkerPassPage })));

/** Pages reachable from emailed links, whether or not someone is signed in. */
const linkRoutes = [
  <Route key="ai" path="/accept-invite" element={<AcceptInvite />} />,
  <Route key="rp" path="/reset-password" element={<ResetPassword />} />,
  // A site worker's own page (QR code + timesheet), from the link sent by WhatsApp: no sign-in.
  <Route key="wp" path="/w/:token" element={<WorkerPassPage />} />,
];

export function App() {
  usePrefetchPages();
  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api<SessionInfo>('/api/auth/session'),
    retry: false,
    // Signed out: nothing to refresh on the sign-in page (checked again only after signing in).
    refetchOnWindowFocus: (q) => q.state.data !== undefined,
    refetchOnReconnect: (q) => q.state.data !== undefined,
  });
  // Once the sign-in page has shown, a passing error (rate limit, a server hiccup) keeps it: the form and what is
  // typed in it stay, instead of "Can't reach the server" and an empty form a moment later.
  const signedOut = useRef(false);
  const is401 = session.error instanceof ApiError && session.error.status === 401;
  if (is401) signedOut.current = true;
  if (session.data) signedOut.current = false;

  if (session.isPending && !signedOut.current) return <div className="boot">Loading…</div>;
  if (session.error || (session.isPending && signedOut.current)) {
    if (is401 || signedOut.current) {
      return (
        <Suspense fallback={pageLoading}><Routes>
          {linkRoutes}
          <Route path="/login" element={<Login mode="login" />} />
          {/* The AI connector's approval page: sign in here, then the page shows the request (the address is kept). */}
          <Route path="/oauth/consent" element={<Login mode="login" />} />
          <Route path="/signup" element={<Login mode="signup" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes></Suspense>
      );
    }
    return <div className="boot">Can't reach the server. Check your connection and reload.</div>;
  }

  if (!session.data) return <div className="boot">Loading…</div>;
  if (session.data.mfa?.required && !session.data.mfa.verified) return <Suspense fallback={pageLoading}><ForcedMfa /></Suspense>;

  return (
    <Suspense fallback={pageLoading}><Routes>
      {linkRoutes}
      <Route path="/oauth/consent" element={<OAuthConsent />} />
      <Route
        path="*"
        element={
          <SessionProvider value={session.data}>
            <AppShell><Suspense fallback={pageLoading}>
              {isAccountsOnly(session.data.permissions) ? (
                // The accounts department without other access: the Accounts tab (as home), projects read-only, settings.
                <Routes>
                  <Route path="/" element={<AccountsPage />} />
                  <Route path="/accounts" element={<Navigate to="/" replace />} />
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/projects/:id" element={<ProjectPage />} />
                  <Route path="/projects/:id/report" element={<ProjectReport />} />
                  <Route path="/projects/:id/period" element={<ProjectPeriodReport />} />
                  <Route path="/projects/:id/timeline" element={<ProjectTimelinePrint />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/expenses" element={<ExpensesPage />} />
                  <Route path="/suggestions" element={<SuggestionsPage />} /><Route path="/exceptions" element={<ExceptionsPage />} />
                  <Route path="/help" element={<Guide />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              ) : isProjectsOnly(session.data.permissions) ? (
                // Project managers and engineers without other access: the Projects area and their own settings only.
                <Routes>
                  <Route path="/" element={isLogisticsOnly(session.data.permissions) ? <LogisticsPage /> : <ProjectsHomePage />} />
                  <Route path="/logistics" element={<LogisticsPage />} />
                  <Route path="/projects/priority/:requestId" element={<PriorityRedirect />} />
                  <Route path="/projects/vo/:voId" element={<VariationRedirect />} />
                  <Route path="/projects/portfolio" element={<ProjectPortfolio />} />
                  <Route path="/projects/profit" element={<ProfitOverviewPage />} />
                  <Route path="/projects/odoo-links" element={<OdooLinksPage />} />
                  <Route path="/projects/manpower" element={<ManpowerPage />} />
                  <Route path="/projects/checkin" element={<SiteCheckinPage />} />
                <Route path="/projects/tools" element={<ToolsPage />} />
                  <Route path="/accounts" element={<AccountsPage />} />
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/projects/:id" element={<ProjectPage />} />
                  <Route path="/projects/:id/report" element={<ProjectReport />} />
                  <Route path="/projects/:id/period" element={<ProjectPeriodReport />} />
                  <Route path="/projects/:id/timeline" element={<ProjectTimelinePrint />} />
                  <Route path="/projects/po/:orderId" element={<OrderRedirect />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/expenses" element={<ExpensesPage />} />
                  <Route path="/suggestions" element={<SuggestionsPage />} /><Route path="/exceptions" element={<ExceptionsPage />} />
                  <Route path="/help" element={<Guide />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              ) : (
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/o/:object" element={<ListView />} />
                <Route path="/o/:object/:id" element={<KeyedRecordPage />} />
                <Route path="/c/:object" element={<CustomListView />} />
                <Route path="/c/:object/:id" element={<CustomRecordPage />} />
                <Route path="/setup" element={<Navigate to="/setup/home" replace />} />
                <Route path="/setup/:section" element={<Setup />} />
                <Route path="/recycle-bin" element={<RecycleBin />} />
                <Route path="/quotes/:id" element={<QuotePage />} />
                <Route path="/import" element={<Import />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/expenses" element={<ExpensesPage />} />
                <Route path="/suggestions" element={<SuggestionsPage />} /><Route path="/exceptions" element={<ExceptionsPage />} />
                  <Route path="/help" element={<Guide />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/kpi" element={<KpiPage />} />
                <Route path="/compliance" element={<CompliancePage />} />
                <Route path="/compliance/:id" element={<CompliancePersonPage />} />
                <Route path="/presales" element={<PresalesPage />} />
                <Route path="/presales/queue" element={<PresalesQueuePage />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/projects/:id" element={<ProjectPage />} />
                <Route path="/projects/:id/report" element={<ProjectReport />} />
                <Route path="/projects/:id/period" element={<ProjectPeriodReport />} />
                <Route path="/projects/:id/timeline" element={<ProjectTimelinePrint />} />
                <Route path="/projects/po/:orderId" element={<OrderRedirect />} />
                <Route path="/projects/priority/:requestId" element={<PriorityRedirect />} />
                <Route path="/projects/home" element={<ProjectsHomePage />} />
                <Route path="/projects/vo/:voId" element={<VariationRedirect />} />
                <Route path="/projects/portfolio" element={<ProjectPortfolio />} />
                <Route path="/projects/profit" element={<ProfitOverviewPage />} />
                <Route path="/projects/odoo-links" element={<OdooLinksPage />} />
                <Route path="/projects/manpower" element={<ManpowerPage />} />
                <Route path="/projects/checkin" element={<SiteCheckinPage />} />
                <Route path="/projects/tools" element={<ToolsPage />} />
                <Route path="/logistics" element={<LogisticsPage />} />
                <Route path="/accounts" element={<AccountsPage />} />
                <Route path="/reports/salesperson" element={<SalespersonReport />} />
                <Route path="/reports/weekly-sales" element={<WeeklySalesPage />} />
                <Route path="/reports/new" element={<ReportBuilder />} />
                <Route path="/reports/:id" element={<ReportBuilder />} />
                <Route path="/dashboards" element={<Dashboards />} />
                <Route path="/dashboards/:id" element={<DashboardPage />} />
                <Route path="/forecasts" element={<Forecasts />} />
                <Route path="/intelligence" element={<Intelligence />} />
                <Route path="/intelligence/history" element={<HistoryReadinessPage />} />
                <Route path="/ceo" element={<CeoPage />} />
                <Route path="/purchasing" element={<PurchasingPage />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/signup" element={<Navigate to="/" replace />} />
                <Route path="/forgot-password" element={<Navigate to="/" replace />} />
                <Route path="*" element={<div className="empty"><h2>Page not found</h2></div>} />
              </Routes>
              )}
            </Suspense></AppShell>
          </SessionProvider>
        }
      />
    </Routes></Suspense>
  );
}
